import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { IMG } from "@/lib/constants";
import { Logo } from "@/components/site/Logo";
import { Seo } from "@/components/site/Seo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="admin-login-page">
      <Seo title="Admin sign in | Ocean Wings Travel" description="Staff sign in." path="/admin/login" noindex />
      <div className="hidden lg:block relative">
        <img src={IMG.lagoon} alt="Andaman lagoon" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A192F]/50" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-display text-5xl font-semibold">Manage every island journey in one place.</p>
          <p className="mt-4 text-slate-200">Enquiries, bookings, prices, quotations and itineraries.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="admin-login-form">
          <Logo />
          <div>
            <h1 className="text-3xl font-bold text-navy">Admin sign in</h1>
            <p className="mt-1 text-sm text-slate-500">Authorised staff only.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="admin-login-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="admin-login-password-input" />
          </div>
          {error && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-ocean hover:bg-navy" data-testid="admin-login-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Sign in</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
